interface NamespaceKey {
  key: string;
  namespaceId: string;
  metadata?: {
    lastFetched: number;
    size?: number;
  };
}

interface KeyValue {
  key: string;
  namespaceId: string;
  value: any;
  metadata: {
    lastFetched: number;
    contentType?: string;
    size?: number;
  };
}

interface CacheConfig {
  maxAge: number; // milliseconds
  maxSize: number; // bytes
  namespaceMaxKeys: Record<string, number>; // namespace-specific limits
}

export class IndexedDBService {
  private db: IDBDatabase | null = null;
  private readonly dbName = 'storage-viewer-db';
  private readonly dbVersion = 1;

  async init(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('namespaceKeys')) {
          const keyStore = db.createObjectStore('namespaceKeys', { keyPath: ['namespaceId', 'key'] });
          keyStore.createIndex('by_namespace', 'namespaceId', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('keyValues')) {
          const valueStore = db.createObjectStore('keyValues', { keyPath: ['namespaceId', 'key'] });
          valueStore.createIndex('by_namespace', 'namespaceId', { unique: false });
        }

        if (!db.objectStoreNames.contains('config')) {
          db.createObjectStore('config');
        }
      };
      
      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  async storeNamespaceKeys(namespaceId: string, keys: string[]): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('namespaceKeys', 'readwrite');
      const store = transaction.objectStore('namespaceKeys');
      
      // Add each key
      keys.forEach(key => {
        store.put({
          key,
          namespaceId,
          metadata: {
            lastFetched: Date.now()
          }
        });
      });
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject((event.target as IDBTransaction).error);
    });
  }

  async getNamespaceKeys(namespaceId: string): Promise<string[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('namespaceKeys', 'readonly');
      const store = transaction.objectStore('namespaceKeys');
      const index = store.index('by_namespace');
      const request = index.getAll(IDBKeyRange.only(namespaceId));
      
      request.onsuccess = () => {
        const results = request.result as NamespaceKey[];
        resolve(results.map(item => item.key));
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async storeKeyValue(namespaceId: string, key: string, value: any): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('keyValues', 'readwrite');
      const store = transaction.objectStore('keyValues');
      
      store.put({
        key,
        namespaceId,
        value,
        metadata: {
          lastFetched: Date.now(),
          contentType: typeof value === 'object' ? 'json' : 'text',
          size: JSON.stringify(value).length
        }
      });
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject((event.target as IDBTransaction).error);
    });
  }

  async getKeyValue(namespaceId: string, key: string): Promise<any | null> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('keyValues', 'readonly');
      const store = transaction.objectStore('keyValues');
      const request = store.get([namespaceId, key]);
      
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.value);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async searchKeys(searchTerm: string): Promise<NamespaceKey[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('namespaceKeys', 'readonly');
      const store = transaction.objectStore('namespaceKeys');
      const request = store.getAll();
      
      request.onsuccess = () => {
        const results = request.result as NamespaceKey[];
        const filteredResults = results.filter(item => 
          item.key.toLowerCase().includes(searchTerm.toLowerCase())
        );
        resolve(filteredResults);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async clearNamespaceCache(namespaceId: string): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['namespaceKeys', 'keyValues'], 'readwrite');
      
      const keyStore = transaction.objectStore('namespaceKeys');
      const valueStore = transaction.objectStore('keyValues');
      
      const keyIndex = keyStore.index('by_namespace');
      const valueIndex = valueStore.index('by_namespace');
      
      const keyRange = IDBKeyRange.only(namespaceId);
      
      // Delete all keys for this namespace
      const keyRequest = keyIndex.openCursor(keyRange);
      keyRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      
      // Delete all values for this namespace
      const valueRequest = valueIndex.openCursor(keyRange);
      valueRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject((event.target as IDBTransaction).error);
    });
  }

  async getConfig(): Promise<CacheConfig> {
    // Default config
    const defaultConfig: CacheConfig = {
      maxAge: 30 * 60 * 1000, // 30 minutes
      maxSize: 50 * 1024 * 1024, // 50MB
      namespaceMaxKeys: {}
    };

    if (!this.db) await this.init();
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction('config', 'readonly');
      const store = transaction.objectStore('config');
      const request = store.get('cacheConfig');
      
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          resolve(defaultConfig);
        }
      };
      
      request.onerror = () => resolve(defaultConfig);
    });
  }

  async cleanupExpiredCache(): Promise<void> {
    if (!this.db) await this.init();
    
    const config = await this.getConfig();
    const now = Date.now();
    const maxAge = config.maxAge;
    
    // Clean keys store
    const keyTransaction = this.db!.transaction('namespaceKeys', 'readwrite');
    const keyStore = keyTransaction.objectStore('namespaceKeys');
    const keyRequest = keyStore.openCursor();
    
    keyRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
      if (cursor) {
        const entry = cursor.value as NamespaceKey;
        if (now - entry.metadata!.lastFetched > maxAge) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    
    // Clean values store
    const valueTransaction = this.db!.transaction('keyValues', 'readwrite');
    const valueStore = valueTransaction.objectStore('keyValues');
    const valueRequest = valueStore.openCursor();
    
    valueRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
      if (cursor) {
        const entry = cursor.value as KeyValue;
        if (now - entry.metadata.lastFetched > maxAge) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    
    return new Promise((resolve) => {
      valueTransaction.oncomplete = () => resolve();
    });
  }
  
  // Methods to support CacheStats
  
  async getAllNamespaceKeys(): Promise<NamespaceKey[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('namespaceKeys', 'readonly');
      const store = transaction.objectStore('namespaceKeys');
      const request = store.getAll();
      
      request.onsuccess = () => {
        resolve(request.result as NamespaceKey[]);
      };
      
      request.onerror = () => reject(request.error);
    });
  }
  
  async getAllKeyValues(): Promise<KeyValue[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('keyValues', 'readonly');
      const store = transaction.objectStore('keyValues');
      const request = store.getAll();
      
      request.onsuccess = () => {
        resolve(request.result as KeyValue[]);
      };
      
      request.onerror = () => reject(request.error);
    });
  }
}

export const indexedDBService = new IndexedDBService();
